/**
 * Registry — the atom ledger's contract (amended by the #740 wipe,
 * 2026-08-20).
 *
 * ATOMS ONLY. The cards array and its plumbing were wiped under the ruled
 * catalog entry `wipe_measurement_presentation_layer` (Chris, 2026-08-18):
 * the card/definition layer was never ratified and carried the project's
 * concentrated drift. The 11 atoms stay — evidence-shaped substrate, extracted
 * daily from Airtable stamps (What / How / When; an atom has no Why — it is a
 * fact, not a judgement). The measurement surface is REBUILT from the Contract
 * (engineering/contract/) after facts/measures are ratified there, generated
 * outward like terms.ts — it does not return here by hand.
 *
 * REVISIONS (#1179; design #1139, ruled #1176). The ledger dates changes to
 * landed atoms in metric_atom_revisions (migration 0020). How it compares and
 * dates them is one contract for every atom, so it sits under its own
 * top-level `revisions` key — never inside `atoms`, where it would read as a
 * twelfth atom. `compared` must equal the extractor's REVISION_COMPARED
 * (apps/worker/src/atoms/extract.ts; the worker's atom test checks).
 *
 * Budgets are enforced at module load. `RegistrySchema.parse(registryJson)`
 * runs below; a budget breach throws at import — the file cannot load
 * dishonest.
 */

import { z } from "zod";
import registryJson from "./registry.json";

/** Per-slot character budgets — an atom answer stays short. */
const BUDGETS = {
  what: 160,
  predicate: 200,
  notes: 300,
} as const;

const AtomTypeSchema = z.enum(["date", "datetime", "derived"]);
const KeySchema = z.enum(["lead", "placement", "of", "hf", "caseType"]);

const SourceFieldSchema = z
  .object({ id: z.string(), name: z.string().min(1) })
  .strict();

const PayloadFieldSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1),
    note: z.string().optional(),
  })
  .strict();

export const AtomSchema = z
  .object({
    id: z.string().min(1),
    what: z.string().min(1),
    type: AtomTypeSchema,
    how: z
      .object({
        sourceFields: z.array(SourceFieldSchema).min(1),
        predicate: z.string().min(1),
      })
      .strict(),
    when: z
      .object({
        cadence: z.literal("daily"),
        /** The provenance entry (engineering/contract/provenance.ts, by id)
         *  that is this atom's own first day, or null = the ledger's ship day
         *  gates it (atom_ledger_ship). Never a date: the worker resolves the
         *  id to its day (routes/registry.ts) and the extractor reads the same
         *  entry through book-predicates.ts — one home for the day. */
        provenance: z.string().nullable(),
      })
      .strict(),
    keys: z.array(KeySchema),
    payloadFields: z.array(PayloadFieldSchema),
    notes: z.string().nullable(),
  })
  .strict();

/** One budgeted slot check against BUDGETS. */
function checkBudget(
  ctx: z.RefinementCtx,
  id: string,
  slot: keyof typeof BUDGETS,
  text: string | null,
  path: (string | number)[],
): void {
  if (text === null) return;
  const cap = BUDGETS[slot];
  if (text.length > cap) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `${id}.${slot} is ${text.length} chars (cap ${cap}) — an atom answer stays short`,
    });
  }
}

/** What a revision compares — the registry's names for the atom's parts. */
const RevisionPartSchema = z.enum([
  "eventDate",
  "outcome",
  "lead",
  "placement",
  "of",
  "hf",
  "caseType",
]);

/** A revision answer stays short too. */
const REVISION_TEXT_CAP = 400;
const RevisionTextSchema = z.string().min(1).max(REVISION_TEXT_CAP);

export const RevisionsSchema = z
  .object({
    what: RevisionTextSchema,
    /** The parts of an atom compared, for every atom type. */
    compared: z.array(RevisionPartSchema).min(1),
    /** What is never compared, and why. */
    notCompared: z
      .array(
        z
          .object({
            atom: z.string().min(1),
            field: z.string().min(1),
            why: RevisionTextSchema,
          })
          .strict(),
      )
      .min(1),
    /** No floor when judging a landed atom (design §4). */
    judgment: RevisionTextSchema,
    /** The detection day and its window (design §2). */
    dated: RevisionTextSchema,
    /** Where a sealed reader lands each row (design §2). */
    landing: RevisionTextSchema,
    /** The provenance entry that is the recorder's first day. */
    provenance: z.string().min(1),
  })
  .strict();

export const RegistrySchema = z
  .object({
    version: z.number().int(),
    atoms: z.array(AtomSchema).min(1),
    revisions: RevisionsSchema,
  })
  .strict()
  .superRefine((reg, ctx) => {
    // Unique atom ids + budgets.
    const atomIds = new Set<string>();
    reg.atoms.forEach((atom, i) => {
      if (atomIds.has(atom.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atoms", i, "id"],
          message: `duplicate atom id "${atom.id}"`,
        });
      }
      atomIds.add(atom.id);
      checkBudget(ctx, atom.id, "what", atom.what, ["atoms", i, "what"]);
      checkBudget(ctx, atom.id, "predicate", atom.how.predicate, [
        "atoms",
        i,
        "how",
        "predicate",
      ]);
      checkBudget(ctx, atom.id, "notes", atom.notes, ["atoms", i, "notes"]);
    });
  });

export type Atom = z.infer<typeof AtomSchema>;
export type Registry = z.infer<typeof RegistrySchema>;
export type Revisions = z.infer<typeof RevisionsSchema>;

/** Parsed + validated at module load — a bad registry.json throws here. */
const registry: Registry = RegistrySchema.parse(registryJson);

export const REGISTRY_VERSION: number = registry.version;
export const ATOMS: readonly Atom[] = registry.atoms;
export const REVISIONS: Revisions = registry.revisions;

/** Atom ids — the spine the extractor's parity guard (S3) checks. */
export const ATOM_IDS: readonly string[] = ATOMS.map((a) => a.id);

const atomIndex = new Map<string, Atom>(ATOMS.map((a) => [a.id, a]));

export const atomById = (id: string): Atom | undefined => atomIndex.get(id);
